import { Box, Group, Text, ThemeIcon, UnstyledButton } from '@mantine/core';
import { IconCheck, IconChevronDown } from '@tabler/icons-react';
import { ReactNode } from 'react';

interface PatientAccessCallBeatProps {
  title: string;
  /** One-line value shown when closed. Empty means untouched, which also clears the tick. */
  summary: string;
  /**
   * Whether the row carries a completion tick. Off for a script whose beats are
   * mostly read rather than answered — a tick that can never light is noise.
   */
  showProgress?: boolean;
  isOpen: boolean;
  onToggle: () => void;
  /**
   * Why the beat doesn't apply, when it doesn't. The row stays, dimmed, with this
   * in the summary slot; it doesn't open.
   */
  disabledReason?: string;
  children: ReactNode;
}

/**
 * One beat of the call script.
 *
 * Every row is one line closed — a title and its value. Headers stay identical
 * whether open or closed; progress is carried by the tick, not by the header.
 */
export function PatientAccessCallBeat({
  title,
  summary,
  showProgress = true,
  isOpen,
  onToggle,
  disabledReason,
  children,
}: PatientAccessCallBeatProps) {
  const disabled = Boolean(disabledReason);
  const open = isOpen && !disabled;
  const shownSummary = disabledReason ?? summary;
  const isDone = !disabled && summary.length > 0;

  return (
    <Box style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
      <UnstyledButton
        // `aria-disabled`, not `disabled`: the row stays in the tab order, and its
        // name carries the reason, so a keyboard or screen-reader user hears why
        // it doesn't apply. Enter and Space reach a button as a click, so this
        // one guard covers them too.
        onClick={disabled ? undefined : onToggle}
        aria-disabled={disabled || undefined}
        w="100%"
        py={9}
        aria-expanded={open}
        style={disabled ? { cursor: 'default' } : undefined}
      >
        <Group gap={10} wrap="nowrap">
          {showProgress && (
            <ThemeIcon
              size={18}
              radius="xl"
              variant={isDone ? 'filled' : 'outline'}
              color={isDone ? 'green.9' : 'gray.3'}
              style={{ flexShrink: 0 }}
            >
              {isDone ? <IconCheck size={11} stroke={3} /> : <span />}
            </ThemeIcon>
          )}

          <Text size="sm" fw={600} c={disabled ? 'dimmed' : undefined} style={{ flex: 1, minWidth: 0 }} truncate>
            {title}
          </Text>

          {!open && shownSummary && (
            <Text size="sm" c="dimmed" truncate style={{ maxWidth: '45%' }}>
              {shownSummary}
            </Text>
          )}

          <IconChevronDown
            size={14}
            color={disabled ? 'var(--mantine-color-dimmed)' : undefined}
            style={{ flexShrink: 0, transition: 'transform 150ms ease', transform: open ? 'rotate(180deg)' : 'none' }}
          />
        </Group>
      </UnstyledButton>

      {/*
        Content sits in from the header either way, so the header reads as the
        header. With a tick the indent is the tick plus its gap (28px), so content
        aligns under the title; without one it is the theme's `lg` step (24px).
      */}
      {open && (
        <Box pl={showProgress ? 28 : 'lg'} pb="sm">
          {children}
        </Box>
      )}
    </Box>
  );
}
